import { Sentry } from "./sentry";

/**
 * Mask a phone number for logging: keep the country code (if present) and the
 * last two digits, replacing the rest with dots. Enough to correlate repeated
 * sends from the same number without storing full PII in Sentry.
 */
export function maskPhone(phone: string): string {
  const trimmed = phone.trim();
  if (trimmed.length <= 4) return "***";
  const cc = trimmed.startsWith("+") ? trimmed.slice(0, 3) : "";
  const last2 = trimmed.slice(-2);
  const hidden = "•".repeat(Math.max(0, trimmed.length - cc.length - 2));
  return `${cc}${hidden}${last2}`;
}

type OtpSendContext = {
  screen: "login" | "profile-setup";
  isResend: boolean;
  phone: string;
};

/**
 * Breadcrumb for every OTP send attempt. Builds the trail Sentry shows leading
 * up to a later captured error, so repeated sends are visible in context.
 */
export function breadcrumbOtpSend({ screen, isResend, phone }: OtpSendContext) {
  Sentry.addBreadcrumb({
    category: "otp",
    level: "info",
    message: `Phone OTP send (${screen})`,
    data: { screen, isResend, phone: maskPhone(phone) },
  });
}

/**
 * Report a failed OTP send as a handled Sentry event with masked context.
 * Accepts either a thrown Error or a better-auth error object ({ message,
 * status, code }).
 */
export function reportOtpSendError(error: unknown, { screen, isResend, phone }: OtpSendContext) {
  const err = error as {
    message?: string;
    status?: number;
    statusText?: string;
    code?: string;
  };
  const message = err?.message ?? "unknown error";

  Sentry.captureMessage(`Phone OTP send failed (${screen}): ${message}`, {
    level: "warning",
    tags: { area: "phone-otp", screen },
    extra: {
      isResend,
      phone: maskPhone(phone),
      status: err?.status,
      statusText: err?.statusText,
      code: err?.code,
    },
  });
}
